import { useState, useEffect, useCallback } from "react";
import { getField, getSelectionRange, removeHighlight } from "./highlightUtils";
import { NewConversation } from "./types";
import {
  useDbtDocsCollaborationDispatch,
  useDbtDocsCollaborationSelector,
} from "./DbtDocsCollaborationProvider";
import { SelectionRange } from "./selectionEndEvent";
import { resetNewConversation } from "./DbtDocsReducer";
import { ConversationGroup } from "./conversations/context/types";

const useHighlight = () => {
  const selectedConversation = useDbtDocsCollaborationSelector(state =>
    state.selectedConversationId ? state.conversations[state.selectedConversationId] : null
  ) as ConversationGroup | null;
  const isDocsAppRendered = useDbtDocsCollaborationSelector(
    state => state.docsAppRendered
  ) as boolean;
  const newConversation = useDbtDocsCollaborationSelector(
    state => state.newConversation
  ) as NewConversation;

  const dispatch = useDbtDocsCollaborationDispatch();
  const [highlightPostData, setHighlightPostData] = useState<NewConversation | null>(null);
  // Set the position to be used to show the highlight popover
  const [pos, setPos] = useState<{
    x: number;
    y: number;
    element: HTMLElement;
  } | null>(null);

  useEffect(() => {
    if (newConversation) {
      setHighlightPostData(null);
      setPos(null);
    }
  }, [newConversation]);

  const resetHighlights = useCallback(() => {
    console.log("resetHighlights");
    if (highlightPostData) {
      removeHighlight(highlightPostData);
    }
    setPos(null);
    setHighlightPostData(null);
  }, [highlightPostData]);

  useEffect(() => {
    if (!selectedConversation || !isDocsAppRendered) {
      return;
    }
    if (selectedConversation.meta.resource_type && selectedConversation.meta.uniqueId) {
      window.location.hash = `#!/${selectedConversation.meta.resource_type}/${selectedConversation.meta.uniqueId}`;
    }
  }, [selectedConversation, isDocsAppRendered, dispatch]);

  const onSelectionEnd = (event: CustomEvent) => {
    const target = event.target as HTMLElement;
    const field = getField(target);
    const { end: selectionEnd, start: selectionStart } = event.detail
      .selectionRange as SelectionRange;
    const selection = document.getSelection();
    if (!selection || !selection.rangeCount) {
      resetHighlights();
      return null;
    }

    const range = selection.getRangeAt(0);

    const highlightedText = range.toString();

    const code = target?.innerText;
    if (!highlightedText || !code) {
      return;
    }

    const { end, start, x, y } = getSelectionRange(
      field,
      target,
      highlightedText,
      selectionEnd,
      selectionStart
    );

    console.log("selection range", end, start, x, y);
    const data = {
      meta: {
        filePath: "",
        field,
        highlight: highlightedText,
        range: {
          end: { line: end, character: 0 },
          start: { line: start, character: 0 },
        },
      },
    };

    dispatch(resetNewConversation());
    setPos({
      x,
      y,
      element: target,
    });

    document.body.addEventListener("click", resetHighlights, { once: true });
    setHighlightPostData(data);
  };

  return {
    getHighlightedSelectionData: () => highlightPostData,
    pos,
    onSelectionEnd,
  };
};

export default useHighlight;
